/**
 * Pure page-geometry and pagination math for the bulk Forms PDF export.
 *
 * Deliberately free of imports and DOM/jsPDF access so the pagination rules can be
 * reasoned about and unit tested on their own — this is the part that decides where
 * a form gets cut, and getting it wrong silently drops or duplicates content.
 *
 * Units: `mm` for the PDF page, `px` for canvas pixels (the rasterised form).
 */

/* -------------------------------------------------------------------- page */

export const PAGE = { width: 210, height: 297, margin: 10 }; // A4 portrait, mm

export const MM_PER_INCH = 25.4;
export const CSS_PX_PER_INCH = 96;
export const PX_PER_MM = CSS_PX_PER_INCH / MM_PER_INCH;
export const CONTENT_WIDTH_MM = PAGE.width - PAGE.margin * 2;

/** Space between the bottom of the repeated header and the top of the form body. */
export const HEADER_GAP_MM = 5;

/** Height the company logo is drawn at; width follows the logo's aspect ratio. */
export const LOGO_HEIGHT_MM = 12;
export const LOGO_MAX_WIDTH_MM = 40;

/** Strip reserved at the foot of every page for the "Generated using" credit. */
export const FOOTER_HEIGHT_MM = 8;
export const BRAND_MARK_HEIGHT_MM = 4.5;

/**
 * The header repeats on every page, so an unbounded goal list would eat the whole
 * document. Goals wrap up to this many lines and are then elided.
 */
export const MAX_GOAL_LINES = 3;

/** Backstop so a pathological form can never spin out thousands of pages. */
const MAX_PAGES_PER_FORM = 200;

/**
 * Where each page of the rasterised form starts/ends, and how it maps onto the page.
 *
 * @param {number} canvasWidthPx - width of the captured canvas, in canvas px
 * @param {number} headerBottomMM - absolute y (mm from page top) where the header ends
 * @param {number} [footerHeightMM] - strip reserved at the foot of every page
 */
export const computeGeometry = (canvasWidthPx, headerBottomMM, footerHeightMM = 0) => {
    const canvasWidthMM = canvasWidthPx / PX_PER_MM;

    // Scale to fit the printable width only — never shrink to force one page.
    const scale = Math.min(CONTENT_WIDTH_MM / canvasWidthMM, 1);

    const contentTopMM = headerBottomMM + HEADER_GAP_MM;
    const availableHeightMM = PAGE.height - contentTopMM - PAGE.margin - footerHeightMM;
    const pageHeightPx = Math.floor((availableHeightMM / MM_PER_INCH) * CSS_PX_PER_INCH / scale);

    return { scale, contentTopMM, availableHeightMM, pageHeightPx };
};

/** Sort and merge overlapping `[top, bottom]` ranges. Touching ranges are left separate. */
export const mergeRanges = (ranges) => {
    const sorted = (ranges || [])
        .filter((r) => Array.isArray(r) && r[1] > r[0])
        .map((r) => [r[0], r[1]])
        .sort((a, b) => a[0] - b[0]);

    const merged = [];
    sorted.forEach((range) => {
        const last = merged[merged.length - 1];
        if (!last || range[0] >= last[1]) merged.push(range);
        else last[1] = Math.max(last[1], range[1]);
    });
    return merged;
};

/**
 * Slice the form into pages, preferring breaks that fall between components and
 * refusing (where possible) to cut through one.
 *
 * @param {object}   args
 * @param {number}   args.contentHeight - canvas px of *content*; trailing whitespace
 *                                        must already be trimmed off, otherwise the
 *                                        tail earns itself a near-blank final page.
 * @param {number}   args.pageHeightPx  - canvas px that fit on one page
 * @param {number[]} args.safeBreaks    - y offsets that are good places to cut
 * @param {number[][]} args.avoidRanges - `[top, bottom]` blocks to keep intact
 * @returns {number[][]} `[[top, bottom], ...]`, contiguous and covering [0, contentHeight)
 */
export const computePageBreaks = ({ contentHeight, pageHeightPx, safeBreaks = [], avoidRanges = [] }) => {
    if (!(contentHeight > 0) || !(pageHeightPx > 0)) return [];

    const breaks = safeBreaks
        .filter((y) => y > 0 && y <= contentHeight)
        .sort((a, b) => a - b);

    // Only protect blocks that could fit on a page in the first place. Merging can
    // glue neighbours into a run taller than a page, so the height filter has to be
    // applied *after* merging as well as before it — otherwise an oversized "atomic"
    // block shoves every break to the top of the page and leaves huge white gaps.
    const clamped = avoidRanges
        .map((r) => [Math.max(0, r[0]), Math.min(contentHeight, r[1])])
        .filter((r) => r[1] > r[0] && r[1] - r[0] <= pageHeightPx);
    const avoid = mergeRanges(clamped).filter((r) => r[1] - r[0] <= pageHeightPx);

    const findAvoid = (y) => {
        let lo = 0;
        let hi = avoid.length - 1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            const [top, bottom] = avoid[mid];
            if (y < top) hi = mid - 1;
            else if (y > bottom) lo = mid + 1;
            else return avoid[mid];
        }
        return null;
    };

    const MIN_CHUNK = Math.min(400, Math.floor(pageHeightPx * 0.4));
    const MAX_OVERFLOW = Math.floor(pageHeightPx * 0.15);

    const slices = [];
    let cursor = 0;

    while (cursor < contentHeight && slices.length < MAX_PAGES_PER_FORM) {
        // Bound to a const so the closures below don't capture the mutating cursor.
        const start = cursor;

        // Everything that's left fits: take it whole. Searching for a break here would
        // only strand the last few hundred pixels on a page of their own.
        if (contentHeight - start <= pageHeightPx) {
            slices.push([start, contentHeight]);
            break;
        }

        const target = Math.min(start + pageHeightPx, contentHeight);

        // Last safe break that still leaves a reasonably full page.
        const candidates = breaks.filter((y) => y >= start + MIN_CHUNK && y <= target);
        let breakAt = candidates.length ? candidates[candidates.length - 1] : null;

        // Nothing usable before the page ends — allow a small overflow to reach one.
        if (breakAt == null) {
            const overflow = breaks.find((y) => y > target && y <= Math.min(target + MAX_OVERFLOW, contentHeight));
            if (overflow != null && overflow - start >= MIN_CHUNK) breakAt = overflow;
        }

        if (breakAt == null) breakAt = target;
        if (breakAt >= contentHeight - 1) breakAt = contentHeight;

        // Don't cut through a component if there's a sane alternative.
        const hit = findAvoid(breakAt - 1);
        if (hit) {
            const [rangeTop, rangeBottom] = hit;
            const moveDown = Math.min(rangeBottom, contentHeight);
            if (moveDown - start >= MIN_CHUNK && moveDown - (start + pageHeightPx) <= MAX_OVERFLOW) {
                breakAt = moveDown;
            } else {
                const moveUp = Math.max(rangeTop, start + MIN_CHUNK);
                if (moveUp - start >= MIN_CHUNK && moveUp <= target) breakAt = moveUp;
                // Neither fits: keep the original break (the block is taller than a page).
            }
        }

        // Two pages left and the second would be a sliver (a form that spills barely
        // past a page boundary): split the remainder down the middle instead, so the
        // last page carries a readable amount of content rather than a thin strip.
        const tail = contentHeight - breakAt;
        if (tail > 0 && tail < pageHeightPx * 0.25 && contentHeight - start <= pageHeightPx * 2) {
            const midpoint = start + Math.floor((contentHeight - start) / 2);
            const reachable = breaks.filter((y) => y > start && y <= target && contentHeight - y <= pageHeightPx);
            const balanced = reachable.length
                ? reachable.reduce((best, y) => (Math.abs(y - midpoint) < Math.abs(best - midpoint) ? y : best))
                : midpoint;
            if (balanced > start && balanced < breakAt && !findAvoid(balanced - 1)) breakAt = balanced;
        }

        // Never go backwards — a stuck cursor would loop forever.
        if (breakAt <= start) breakAt = target;
        if (breakAt <= start) break;

        slices.push([start, breakAt]);
        cursor = breakAt;
    }

    return slices;
};
